import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronDown, Info } from 'lucide-react';
import type { ModuleCode, TaxCategory } from '@his/shared';
import { useQuery } from '@tanstack/react-query';
import { TAX_CATEGORY_LABEL } from '@/components/TaxCategorySelect';
import { get, qs } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { humanize, money } from '@/lib/utils';
import { DataTable, FilterField, PageHeader, SearchFilterBar, Tabs } from '@/components/ui/layout';
import { Alert, Badge, Button, Checkbox, Input, Popover, PopoverContent, PopoverTrigger, Select, Tooltip } from '@/components/ui/primitives';
import { DoctorsWithoutFeeAlert } from './DoctorsWithoutFeeAlert';
import { UnpricedOrdersAlert } from './UnpricedOrdersAlert';
import { PackagesTable, type ServicePackage } from './PackagesTab';

export interface ChargeItem {
  id: string;
  code: string;
  name: string;
  nameLocal?: string | null;
  branchId?: string | null;
  category: string;
  basePriceMinor: number;
  taxPct: number;
  taxCategory?: string | null;
  benefitCategory?: string | null;
  durationMinutes: number;
  nphiesClaimable: boolean;
  department: { id: string; name: string } | null;
  departments: { id: string; name: string }[];
  nphiesPriceMinor?: number | null;
  nphiesOverrides?: number;
}

export interface DepartmentOption {
  id: string;
  name: string;
  branchId?: string | null;
}

export interface Tariff {
  id: string;
  code: string;
  name: string;
  kind?: string;
  categories: string[];
  priority: number;
  planId?: string | null;
  forBranchId?: string | null;
  validFrom?: string | null;
  validTo?: string | null;
  payer: { id: string; name: string } | null;
  _count: { prices: number };
}

/**
 * Charge categories, and the module each one belongs to.
 *
 * The list used to be fixed, so a medical store pricing its shelf was offered
 * "bed", "radiology" and "consultation" — categories for work it does not do
 * and cannot bill. `null` means every facility has it.
 */
const CATEGORIES: { value: string; needs: ModuleCode[] | null }[] = [
  { value: 'consultation', needs: ['opd', 'er'] },
  { value: 'procedure', needs: null },
  { value: 'bed', needs: ['ipd'] },
  { value: 'nursing', needs: ['ipd', 'opd', 'er'] },
  { value: 'ot', needs: ['ot'] },
  { value: 'lab', needs: ['lab', 'opd', 'ipd', 'er'] },
  { value: 'radiology', needs: ['radiology', 'opd', 'ipd', 'er'] },
  { value: 'pharmacy', needs: ['pharmacy'] },
  { value: 'package', needs: null },
  { value: 'misc', needs: null },
];

/** The categories a facility holding these modules can raise a charge for. */
export function chargeCategoriesFor(hasModule: (module: ModuleCode) => boolean): string[] {
  return CATEGORIES.filter((c) => c.needs === null || c.needs.some(hasModule)).map((c) => c.value);
}

export function useChargeCategories(): string[] {
  const { hasModule } = useAuth();
  return chargeCategoriesFor(hasModule);
}

export function useNphies(): boolean {
  const { hasModule, user } = useAuth();
  return hasModule('insurance') && (user?.branches ?? []).some((branch) => !branch.country || branch.country === 'SA');
}

function durationLabel(minutes: number): string {
  return minutes > 0 ? tr('billing', 'x_min', { minutes }) : tr('billing', 'ask_at_booking');
}

function departmentNames(row: ChargeItem): string {
  const linked = row.departments.length ? row.departments : row.department ? [row.department] : [];
  return linked.length ? linked.map((department) => department.name).join(', ') : '—';
}

function NphiesCell({ row }: { row: ChargeItem }) {
  if (!row.nphiesClaimable) return <span className="text-muted-foreground">—</span>;
  const overrides = row.nphiesOverrides ?? 0;
  const price = (
    <span className="inline-flex items-center gap-2">
      <Badge variant="secondary">{tr('billing', 'nphies')}</Badge>
      <span className="tabular">{money(row.nphiesPriceMinor ?? row.basePriceMinor)}</span>
      {overrides > 0 && <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />}
    </span>
  );
  if (overrides === 0) return price;
  return <Tooltip content={tr('billing', 'payer_price_lists_that_override_this_amount', { count: overrides })}>{price}</Tooltip>;
}

export function DepartmentsPicker({ options, value, onChange }: { options: DepartmentOption[]; value: string[]; onChange: (ids: string[]) => void }) {
  const [search, setSearch] = useState('');
  const chosen = new Set(value);
  const needle = search.trim().toLowerCase();
  const shown = needle ? options.filter((option) => option.name.toLowerCase().includes(needle)) : options;
  const label =
    value.length === 0
      ? tr('billing', 'no_departments')
      : value.length === 1
        ? (options.find((option) => option.id === value[0])?.name ?? tr('billing', 'x_departments', { count: 1 }))
        : tr('billing', 'x_departments', { count: value.length });

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-between font-normal">
          <span className="truncate">{label}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-2">
        {options.length > 8 && <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={tr('billing', 'search_departments')} autoFocus />}
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {shown.map((option) => (
            <label key={option.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-accent">
              <Checkbox
                checked={chosen.has(option.id)}
                onCheckedChange={(on) => onChange(on ? [...value, option.id] : value.filter((id) => id !== option.id))}
              />
              <span className="min-w-0 flex-1 truncate">{option.name}</span>
            </label>
          ))}
          {shown.length === 0 && <p className="px-1 py-1 text-sm text-muted-foreground">{tr('billing', 'no_departments')}</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Charge master and tariffs (SRS 9.1) — the price list every module bills from. */
const TABS = ['charges', 'tariffs', 'packages'];

export function ChargeMasterPage() {
  const { can, hasModule } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(() => {
    const wanted = params.get('tab');
    return wanted && TABS.includes(wanted) ? wanted : 'charges';
  });
  const [needsPrice, setNeedsPrice] = useState(() => params.get('needsPrice') === '1');
  const returnTo = `/billing/charges?tab=${tab}${tab === 'charges' && needsPrice ? '&needsPrice=1' : ''}`;
  const openScreen = (path: string) => navigate(`${path}?returnTo=${encodeURIComponent(returnTo)}`);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const categories = useChargeCategories();
  const usesNphies = useNphies();

  const charges = useQuery({
    queryKey: ['charge-items', search, category, usesNphies, needsPrice],
    queryFn: () =>
      get<{ data: ChargeItem[] }>(
        `/charge-items${qs({
          q: search || undefined,
          category: category || undefined,
          withNphies: usesNphies ? 1 : undefined,
          needsPrice: needsPrice ? 1 : undefined,
          limit: 300,
        })}`,
      ),
  });

  const startersUnpriced =
    !search && !category && !needsPrice && Boolean(charges.data?.data.length) && Boolean(charges.data?.data.every((row) => row.basePriceMinor === 0));

  const tariffs = useQuery({
    queryKey: ['tariffs'],
    queryFn: () => get<Tariff[]>('/tariffs'),
    enabled: tab === 'tariffs',
  });

  const packages = useQuery({
    queryKey: ['packages'],
    queryFn: () => get<ServicePackage[]>('/packages'),
    enabled: tab === 'packages',
  });

  return (
    <>
      <PageHeader
        title={tr('billing', 'charge_master')}
        description={tr('billing', 'services_their_list_prices_and_the_tariffs_that')}
        actions={
          can('charge.manage') &&
          (tab === 'charges' ? (
            <Button onClick={() => openScreen('/billing/charges/services/new')}>{tr('billing', 'add_service')}</Button>
          ) : tab === 'tariffs' ? (
            <Button onClick={() => openScreen('/billing/charges/price-lists/new')}>{tr('billing', 'add_tariff')}</Button>
          ) : tab === 'packages' ? (
            <Button onClick={() => openScreen('/billing/charges/packages/new')}>{tr('billing', 'add_package')}</Button>
          ) : null)
        }
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'charges', label: tr('billing', 'services') },
          { id: 'tariffs', label: tr('billing', 'tariffs') },
          { id: 'packages', label: tr('billing', 'packages') },
        ]}
      />

      {tab === 'charges' && (
        <>
          {hasModule('opd') && can('charge.read', 'charge.manage') && <DoctorsWithoutFeeAlert />}
          {!needsPrice && !startersUnpriced && (
            <UnpricedOrdersAlert
              className="mb-3"
              onShow={() => {
                setCategory('');
                setNeedsPrice(true);
              }}
            />
          )}
          <SearchFilterBar
            search={{ value: search, onChange: setSearch, placeholder: tr('billing', 'search_services') }}
            filters={
              <>
                <FilterField label={tr('billing', 'category')}>
                  <Select className="w-48" value={category} onChange={(e) => setCategory(e.target.value)}>
                    <option value="">{tr('billing', 'all_categories')}</option>
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {humanize(c, 'charge_category')}
                      </option>
                    ))}
                  </Select>
                </FilterField>
                <FilterField label={tr('billing', 'price_filter')}>
                  <Select className="w-40" value={needsPrice ? 'needs' : ''} onChange={(e) => setNeedsPrice(e.target.value === 'needs')}>
                    <option value="">{tr('billing', 'any_price')}</option>
                    <option value="needs">{tr('billing', 'needs_price')}</option>
                  </Select>
                </FilterField>
              </>
            }
            onReset={
              search || category || needsPrice
                ? () => {
                    setSearch('');
                    setCategory('');
                    setNeedsPrice(false);
                  }
                : undefined
            }
          />

          {startersUnpriced && (
            <Alert variant="warning" className="mb-3">
              {tr('billing', 'set_your_prices_the_starter_services_were_added')}
            </Alert>
          )}

          <DataTable
            rows={charges.data?.data}
            loading={charges.isLoading} refreshing={charges.isFetching}
            error={charges.error}
            rowKey={(row) => row.id}
            onRowClick={can('charge.manage') ? (row) => openScreen(`/billing/charges/services/${row.id}/edit`) : undefined}
            empty={tr('billing', 'no_services_in_the_charge_master_yet')}
            columns={[
              { header: tr('billing', 'code'), width: '130px', sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
              { header: tr('billing', 'service'), sort: (row) => row.name, cell: (row) => row.name },
              { header: tr('billing', 'category'), sort: (row) => row.category, cell: (row) => <Badge variant="secondary">{humanize(row.category, 'charge_category')}</Badge> },
              { header: tr('billing', 'departments'), sort: (row) => departmentNames(row), cell: (row) => departmentNames(row) },
              { header: tr('billing', 'duration'), align: 'right', sort: (row) => row.durationMinutes, cell: (row) => durationLabel(row.durationMinutes) },
              { header: tr('billing', 'tax'), sort: (row) => row.taxCategory ?? '', cell: (row) => (row.taxCategory ? TAX_CATEGORY_LABEL[row.taxCategory as TaxCategory] ?? humanize(row.taxCategory, 'tax_category') : tr('billing', 'country_default')) },
              { header: tr('billing', 'list_price'), align: 'right', sort: (row) => row.basePriceMinor, cell: (row) => money(row.basePriceMinor) },
              ...(usesNphies
                ? [{ header: tr('billing', 'nphies'), cell: (row: ChargeItem) => <NphiesCell row={row} /> }]
                : []),
            ]}
          />
        </>
      )}

      {tab === 'tariffs' && (
        <DataTable
          rows={tariffs.data}
          loading={tariffs.isLoading} refreshing={tariffs.isFetching}
          error={tariffs.error}
          rowKey={(row) => row.id}
          onRowClick={can('charge.manage') ? (row) => openScreen(`/billing/charges/price-lists/${row.id}`) : undefined}
          empty={tr('billing', 'no_tariffs_defined')}
          columns={[
            { header: tr('billing', 'code'), sort: (row) => row.code, cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
            {
              header: tr('billing', 'tariff'),
              sort: (row) => row.name,
              cell: (row) =>
                row.kind === 'nphies' ? (
                  <span className="inline-flex items-center gap-2">
                    {row.name}
                    <Badge variant="secondary">{tr('billing', 'auto')}</Badge>
                  </span>
                ) : (
                  row.name
                ),
            },
            {
              header: tr('billing', 'applies_to'),
              cell: (row) => (
                <div className="flex flex-wrap gap-1">
                  {row.kind === 'nphies' && <Badge variant="secondary">{tr('billing', 'nphies')}</Badge>}
                  {row.categories.map((c) => (
                    <Badge key={c} variant="secondary">
                      {humanize(c, 'patient_category')}
                    </Badge>
                  ))}
                </div>
              ),
            },
            { header: tr('billing', 'payer'), sort: (row) => row.payer?.name, cell: (row) => row.payer?.name ?? '—' },
            { header: tr('billing', 'priority'), align: 'right', sort: (row) => row.priority, cell: (row) => row.priority },
            { header: tr('billing', 'priced_services'), align: 'right', sort: (row) => row._count.prices, cell: (row) => row._count.prices },
          ]}
        />
      )}

      {tab === 'packages' && (
        <PackagesTable
          rows={packages.data}
          loading={packages.isLoading}
          error={packages.error}
          onEdit={can('charge.manage') ? (row) => openScreen(`/billing/charges/packages/${row.id}/edit`) : undefined}
        />
      )}
    </>
  );
}
